/**
 * ForeignKeyPopover Component
 *
 * Displays related data from a referenced table when hovering/clicking on an FK cell.
 */

// TODO(slice-7): ForeignKeyPopover is over 450 lines. Once the EP1
// breadcrumb behavior is stable, split this file into the popover shell,
// the loose-FK footer wiring, and the open-action handlers. Out of scope
// for the Slice 3 review fixes.
import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Link2, Maximize2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import { Button } from '../../../../components/ui/button';
import { toast } from '../../../../components/ui/toaster';
import { useForeignKeyData } from '../hooks/useForeignKeyData';
import { openTableExplorerTab } from '../utils/openTableExplorerTab';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useAppStore } from '../../../../store/appStore';
import { getEngineForConnection } from '../../../connections/utils/connectionLookup';
import { isTableExplorerTab } from '../../core/types/workspaceTypes';
import { LooseRelationshipFooter } from './LooseRelationshipFooter';
import { ReferenceTableFooter } from './ReferenceTableFooter';
import { ForeignKeyPopoverContent } from './ForeignKeyPopoverContent';
import { LooseRelationshipEditorDialog } from '../../common/components/filters/LooseRelationshipEditorDialog';
import { useMetaInfo } from '../../common/hooks/useMetaInfo';
import { useAvailableTables } from '../hooks/useAvailableTables';
import type { LooseRelationship } from '../../common/services/metaInfoApi';
import { renderLooseFkLabel } from '../../common/utils/looseFkLabels';
import { DrillOpenInMenu } from '../../common/components/DrillOpenInMenu';
import { useFkBreadcrumbFirstRunHint } from './FkBreadcrumb/FkBreadcrumbFirstRunHint';
import {
  makeTableNode,
  initTree,
} from './FkBreadcrumb/breadcrumbTree';
import { pushRowOnto } from './FkBreadcrumb/tabNavigationTreeHelpers';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { TableExplorerRelationshipPort } from './tableExplorerHostPorts';
import { HostRelationshipTables, type RelationshipEditorTable } from './HostRelationshipTables';

type ForeignKeyPopoverProps = {
  children: React.ReactNode;
  connectionId: string;
  database: string;
  referencedSchema: string;
  referencedTable: string;
  referencedColumn: string;
  value: unknown;
  /** When true, open the popover immediately on mount (used by LazyForeignKeyCell after constraint resolution). */
  defaultOpen?: boolean;
  /**
   * When true, the FK is user-declared via MetaInfo, not a real DB constraint.
   * Adds a "Clear loose relation" footer action and dashed/teal styling on the trigger.
   */
  isLoose?: boolean;
  /** Origin column coordinates — required to look up & remove the loose relationship by id. */
  sourceSchema?: string;
  sourceTable?: string;
  sourceColumn?: string;
  /** Surface where this popover is rendered — emitted on the popover-opened analytics event. */
  surface?: 'query_editor' | 'table_explorer';
  /** Full source row data — threaded from the grid for use by filter-aware loose FK logic (Task 3.5). */
  sourceRow?: Record<string, unknown>;
  /**
   * All loose FK relationships that match the current source column AND pass
   * source-row filter gating (Task 3.3). Read only for a LOOSE FK cell: on a
   * real FK cell (`isLoose` false) the constraint wins over every declaration
   * on the column (`realFkOutranksLoose`) — a conflicting one's target and an
   * identical-endpoint one's `targetFilter` alike.
   */
  matchingLooseRels?: LooseRelationship[];
  relationshipPort?: TableExplorerRelationshipPort;
};

function normalizeFilterValue(value: unknown): string | number | boolean | Date | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value;
  return JSON.stringify(value);
}

export function ForeignKeyPopover({
  children,
  connectionId,
  database,
  referencedSchema,
  referencedTable,
  referencedColumn,
  value,
  defaultOpen = false,
  isLoose = false,
  sourceSchema,
  sourceTable,
  sourceColumn,
  surface,
  matchingLooseRels: matchingLooseRelsProp,
  relationshipPort,
}: ForeignKeyPopoverProps) {
  // A real FK cell previews, navigates and edits through its constraint only.
  const matchingLooseRels = isLoose ? matchingLooseRelsProp : undefined;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // The Edit / Add-another flows open a full Dialog (LooseRelationshipEditorDialog)
  // because the popover doesn't have enough room for the picker + 2 filter
  // panes + peek pane. The popover closes when the dialog opens so the user
  // isn't left with a stale floating popover behind the modal.
  const [editorMode, setEditorMode] = useState<'edit' | 'add' | null>(null);
  const [activeFkId, setActiveFkId] = useState<string | undefined>(
    matchingLooseRels?.[0]?.id,
  );
  const { track } = useAnalytics();

  // Slice 3 — replace the previous `window.__SQL_STUDIO_FK_OPEN_*`
  // trampolines with direct Zustand selectors + the first-run-hint hook.
  // The TE tab no longer needs to install/clean up window globals on mount.
  const fkOpenDefault = useAppStore((s) => s.fkOpenDefault);
  const triggerFirstRunHint = useFkBreadcrumbFirstRunHint();

  // Plumbed engine for analytics + future SQL emission. Lazily resolved from
  // the connection store at call time so we don't subscribe to engine
  // changes (engine is immutable per connection).
  const resolveEngine = (): DatabaseEngineId =>
    (getEngineForConnection(connectionId) as DatabaseEngineId) || 'mssql';

  useEffect(() => {
    if (!matchingLooseRels?.length) return;
    if (!activeFkId || !matchingLooseRels.find((r) => r.id === activeFkId)) {
      setActiveFkId(matchingLooseRels[0].id);
    }
  }, [matchingLooseRels, activeFkId]);

  const activeFk = matchingLooseRels?.find((r) => r.id === activeFkId);

  const handleOpenChange = (next: boolean) => {
    if (next && !isOpen) {
      track('feature:fk_cell_popover_opened', { isLoose, surface: surface ?? 'unknown' });
      if (matchingLooseRels && matchingLooseRels.length > 1) {
        track('feature:loose_fk_multi_fk_resolved', { count: matchingLooseRels.length });
      }
    }
    setIsOpen(next);
  };

  // Loose-FK edit mode needs (a) the matching MetaInfo entry's id +
  // current target so the picker opens pre-filled, and (b) the list of
  // available tables for the table dropdown. Both are TanStack-cached.
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  const looseRel = useMemo(() => {
    // `sourceSchema` is `''` on schemaless engines (Kusto/ADX) — present, not
    // missing. `== null` keeps loose-FK resolution working there; a truthy
    // check would disable navigate/edit for every Kusto loose FK.
    if (!isLoose || sourceSchema == null || !sourceTable || !sourceColumn) return undefined;
    return (metaInfo?.looseRelationships ?? []).find(
      (r) =>
        r.fromSchema === sourceSchema &&
        r.fromTable === sourceTable &&
        r.fromColumn === sourceColumn,
    );
  }, [isLoose, metaInfo, sourceSchema, sourceTable, sourceColumn]);

  const tablesQuery = useAvailableTables({
    connectionId,
    database,
    enabled: editorMode !== null && !relationshipPort,
  });
  // A host port lists its tables through `HostRelationshipTables`, mounted only
  // while the editor dialog is open (S4 review F, F6) — never an idle observer
  // per FK cell. SQL: exactly the base listing.
  const allTables = useMemo(
    () =>
      (tablesQuery.data ?? []).map((t) => ({
        schema: t.schema,
        name: t.name,
        columns: [] as { name: string; dataType: string }[],
      })),
    [tablesQuery.data],
  );

  const effectiveReferencedSchema = activeFk?.toSchema ?? referencedSchema;
  const effectiveReferencedTable = activeFk?.toTable ?? referencedTable;
  const effectiveReferencedColumn = activeFk?.toColumn ?? referencedColumn;
  const effectiveFullTableName = effectiveReferencedSchema
    ? `${effectiveReferencedSchema}.${effectiveReferencedTable}`
    : effectiveReferencedTable;
  // Host ports say why a relationship cannot be navigated (S4 review F, F11 —
  // an Azure Table target without a single counterpart key filter): Open is
  // then unavailable with that reason instead of silently doing nothing.
  const hostOpenUnavailable = relationshipPort?.unresolvableReason({
    referencedColumn: effectiveReferencedColumn,
    value,
    targetFilter: activeFk?.targetFilter,
    declarationId: activeFk?.id,
  });

  const { data, isLoading, error } = useForeignKeyData({
    connectionId,
    database,
    referencedSchema: effectiveReferencedSchema,
    referencedTable: effectiveReferencedTable,
    referencedColumn: effectiveReferencedColumn,
    value,
    enabled: isOpen && value !== null && value !== undefined,
    targetFilter: activeFk?.targetFilter,
    declarationId: activeFk?.id,
    relationshipPort,
  });

  /** Open a NEW Table Explorer tab on the FK target (legacy behavior). */
  const handleOpenInNewExplorer = () => {
    // Host ports navigate with the raw value: an Edm.Int64 `bigint` must not
    // go through the SQL filter normalizer's JSON fallback (S4 review C, M2).
    if (relationshipPort) {
      if (value === null || value === undefined) {
        toast.error({ title: 'FK navigation failed', description: 'Foreign key value is NULL' });
        return;
      }
      if (hostOpenUnavailable) {
        toast.error({ title: 'FK navigation unavailable', description: hostOpenUnavailable });
        return;
      }
      void relationshipPort.openReferencedRow({
        connectionId,
        database,
        referencedSchema: effectiveReferencedSchema,
        table: effectiveReferencedTable,
        referencedColumn: effectiveReferencedColumn,
        value,
        targetFilter: activeFk?.targetFilter,
        declarationId: activeFk?.id,
        row: data?.row ?? undefined,
      });
      setIsOpen(false);
      return;
    }

    const filterValue = normalizeFilterValue(value);
    if (filterValue === null) {
      toast.error({ title: 'FK navigation failed', description: 'Foreign key value is NULL' });
      return;
    }

    openTableExplorerTab({
      connectionId,
      database,
      schema: effectiveReferencedSchema,
      tableName: effectiveReferencedTable,
      title: `${effectiveFullTableName} [FK]`,
      filters: [
        {
          field: effectiveReferencedColumn,
          conditions: [{ operator: '=', value: filterValue }],
        },
        ...(activeFk?.targetFilter ?? []),
      ],
    });

    setIsOpen(false);
  };

  /** Primary "Open" — drill in (TE context) or fall back to new explorer (QE context per EC1). */
  const handleOpenPrimary = () => {
    if (relationshipPort) {
      if (value === null || value === undefined) {
        toast.error({ title: 'FK navigation failed', description: 'Foreign key value is NULL' });
        return;
      }
      if (hostOpenUnavailable) return; // the popover shows the reason next to the unavailable Open
      track('feature:fk_nav_open_new_explorer', {
        source: 'fk-popover',
        targetKind: 'row',
        engine: resolveEngine(),
        treeWasEmpty: false,
      });
      void relationshipPort.openReferencedRow({
        connectionId,
        database,
        referencedSchema: effectiveReferencedSchema,
        table: effectiveReferencedTable,
        referencedColumn: effectiveReferencedColumn,
        value,
        targetFilter: activeFk?.targetFilter,
        declarationId: activeFk?.id,
        row: data?.row ?? undefined,
      });
      setIsOpen(false);
      return;
    }

    const filterValue = normalizeFilterValue(value);
    if (filterValue === null) {
      toast.error({ title: 'FK navigation failed', description: 'Foreign key value is NULL' });
      return;
    }

    const engine = resolveEngine();

    // Determine the current TE tab (if any). FK popovers rendered from QE
    // results don't have a parent TE tab — we fall back to new-tab behavior.
    const ws = useWorkspaceStore.getState();
    const active = ws.tabs.find((t) => t.id === ws.activeTabId);
    const isTeContext = surface === 'table_explorer' && active && isTableExplorerTab(active);

    if (!isTeContext || fkOpenDefault === 'new-tab') {
      // EC1 path: QE context → open new tab. `source: 'fk-popover'`
      // disambiguates this from the ⋯ submenu (`fk-popover-menu`).
      const treeWasEmpty = !isTeContext;
      track('feature:fk_nav_open_new_explorer', {
        source: 'fk-popover',
        targetKind: 'row',
        engine,
        treeWasEmpty,
      });
      handleOpenInNewExplorer();
      return;
    }

    // TE context — drill in.
    const tabId = active.id;
    const tabContent = active.content;
    const treeWasEmpty = !tabContent.navigationTree || tabContent.navigationTree.rootId === tabContent.navigationTree.activeNodeId;

    // Build the new tree:
    //  - if no tree yet, root = current TE table.
    //  - if tree exists, push onto the active node.
    const baseTree =
      tabContent.navigationTree ??
      initTree(
        makeTableNode({
          schema: tabContent.schema,
          table: tabContent.tableName,
          filters: [],
        }),
      );
    // `pushRowOnto` dedups against the active node's direct children: clicking
    // the same FK value twice from the same parent activates the existing
    // chip instead of pushing a duplicate row-node. Raw `push(...)` (used
    // here before) didn't have the dedup, producing two `#803` chips when the
    // user re-opened the same FK value on the same parent row.
    const { tree: nextTree } = pushRowOnto(baseTree, {
      schema: effectiveReferencedSchema,
      table: effectiveReferencedTable,
      pk: [{ column: effectiveReferencedColumn, value: filterValue }],
    });
    ws.updateTabContent(tabId, { navigationTree: nextTree });

    track('feature:fk_nav_drilldown', {
      source: 'fk-popover',
      targetKind: 'row',
      direction: 'forward',
      treeWasEmpty,
      viaLooseFk: isLoose,
      joinCollapsed: false,
      engine,
    });

    // First-run hint trigger — fires the toast once per appStore
    // `hasSeenFkBreadcrumbHint` flag flip. Internal short-circuit on second
    // call. (Previously the trigger was a window global installed by
    // TableExplorerTab; Slice 3 review moves it to a direct hook.)
    try {
      triggerFirstRunHint();
    } catch {
      // Never throw inside the FK popover flow.
    }

    setIsOpen(false);
  };

  const triggerClass = isLoose
    ? 'inline-flex items-center gap-1 min-w-0 text-teal-600 dark:text-teal-400 hover:text-teal-500 underline decoration-dashed decoration-teal-500/60 underline-offset-2 cursor-pointer'
    : 'inline-flex items-center gap-1 min-w-0 text-blue-600 dark:text-blue-400 hover:underline cursor-pointer';
  const triggerTitle = isLoose
    ? 'Loose relationship — click to view related row'
    : `View related row in ${effectiveFullTableName}`;
  const TriggerIcon = isLoose ? Link2 : ExternalLink;

  const openEditor = (mode: 'edit' | 'add') => {
    setEditorMode(mode);
    setIsOpen(false);
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            className={triggerClass}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenChange(true);
            }}
            title={triggerTitle}
            data-testid="fk-cell-trigger"
            data-loose={isLoose ? 'true' : 'false'}
          >
            {children}
            <TriggerIcon className="h-3 w-3 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 p-0"
          align="start"
          data-testid="fk-popover"
          data-loose={isLoose ? 'true' : 'false'}
        >
          <div className="flex flex-col">
            {/* Multi-FK tab strip */}
            {matchingLooseRels && matchingLooseRels.length > 1 && (
              <div className="flex gap-1 px-3 py-1 border-b border-border bg-muted/30" data-testid="loose-fk-tabs">
                {matchingLooseRels.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      setActiveFkId(r.id);
                      track('feature:loose_fk_popover_tab_switch');
                    }}
                    className={
                      r.id === activeFkId
                        ? 'px-2 py-0.5 text-xs rounded bg-primary text-primary-foreground'
                        : 'px-2 py-0.5 text-xs rounded border'
                    }
                    data-testid={`loose-fk-tab-${r.id}`}
                  >
                    {renderLooseFkLabel(r)}
                  </button>
                ))}
              </div>
            )}

            {/* Header */}
            <div className="px-3 py-2 border-b border-border bg-muted/30">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-muted-foreground">
                    {isLoose ? 'Related row from (loose)' : 'Related row from'}
                  </div>
                  <div className="text-sm font-medium truncate">
                    {effectiveFullTableName}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* QE-rendered popovers (`surface !== 'table_explorer'`) have
                      no parent TE tab to drill into — the standalone "Open"
                      becomes redundant. Collapse it into the menu trigger
                      (rendered as "Open ⌄") so the popover surfaces only the
                      two explicit destinations: new explorer / query editor. */}
                  {surface === 'table_explorer' && (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      className={`h-7 px-2 text-xs${hostOpenUnavailable ? ' opacity-50 cursor-not-allowed' : ''}`}
                      onClick={handleOpenPrimary}
                      title={hostOpenUnavailable ?? 'Open in Table Explorer (drill in)'}
                      data-testid="fk-popover-open"
                      {...(hostOpenUnavailable ? { 'aria-disabled': true } : {})}
                    >
                      <Maximize2 className="h-3.5 w-3.5 mr-1" />
                      Open
                    </Button>
                  )}
                  {!relationshipPort && (() => {
                    const filterValue = normalizeFilterValue(value);
                    if (filterValue === null) return null;
                    return (
                      <DrillOpenInMenu
                        connectionId={connectionId}
                        database={database}
                        engineId={resolveEngine()}
                        source="fk-popover-menu"
                        target={{
                          kind: 'row',
                          schema: effectiveReferencedSchema,
                          table: effectiveReferencedTable,
                          pk: [{ column: effectiveReferencedColumn, value: filterValue }],
                        }}
                        buttonTestId="fk-popover-more-menu"
                        variant={surface === 'table_explorer' ? 'kebab' : 'labeled'}
                        onPerformed={() => setIsOpen(false)}
                      />
                    );
                  })()}
                </div>
              </div>
            </div>

            {hostOpenUnavailable && (
              <div className="px-3 py-1.5 border-b border-border text-xs text-muted-foreground" role="note" data-testid="fk-popover-open-unavailable">
                {hostOpenUnavailable}
              </div>
            )}

            {/* Content — row data render delegated to a focused subcomponent. */}
            <div className="p-3 max-h-64 overflow-y-auto">
              <ForeignKeyPopoverContent isLoading={isLoading} error={error} data={data} />
            </div>

            {/* Footer — only for loose-FK cells (handles MetaInfo lookup + remove).
                "Add another" lives on the editor dialog, not here, so the
                footer stays single-row and compact. */}
            {isLoose && (
              <LooseRelationshipFooter
                connectionId={connectionId}
                database={database}
                sourceSchema={sourceSchema}
                sourceTable={sourceTable}
                sourceColumn={sourceColumn}
                onCleared={() => setIsOpen(false)}
                onEditRequested={() => openEditor('edit')}
                activeFk={activeFk}
              />
            )}

            {/* "Show names from <target>" — the on-ramp to (and off-ramp from)
                the reference-table flag. Real AND loose FKs: the question is
                about the TARGET table, not about how the relationship was
                declared, so a loose FK gets it under its own footer. */}
            {!relationshipPort && (
              <ReferenceTableFooter
                connectionId={connectionId}
                database={database}
                schema={effectiveReferencedSchema}
                table={effectiveReferencedTable}
                keyColumn={effectiveReferencedColumn}
                engine={resolveEngine()}
                onRemoved={() => setIsOpen(false)}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Edit / Add-another launches a proper Dialog so the picker has room
          for the filter panes + peek pane (the popover was too narrow).
          Edit targets the *active* tab's FK when N>1 declarations exist on
          this column (multi-FK popover); falls back to the first match. */}
      {editorMode !== null && sourceSchema != null && sourceTable && sourceColumn && (() => {
        const editTarget = activeFk ?? looseRel;
        const editorDialog = (tables: RelationshipEditorTable[]) => (
          <LooseRelationshipEditorDialog
            open={editorMode !== null}
            onOpenChange={(next) => {
              if (!next) setEditorMode(null);
            }}
            connectionId={connectionId}
            database={database}
            source={{
              schema: sourceSchema,
              table: sourceTable,
              name: sourceColumn,
              dataType: '',
            }}
            allTables={tables}
            metadataEditors={relationshipPort}
            existingRelationshipId={editorMode === 'edit' ? editTarget?.id : undefined}
            initialTarget={
              editorMode === 'edit' && editTarget
                ? {
                    schema: editTarget.toSchema,
                    table: editTarget.toTable,
                    column: editTarget.toColumn,
                  }
                : undefined
            }
            initialSourceFilter={
              editorMode === 'edit' ? editTarget?.sourceFilter : undefined
            }
            initialTargetFilter={
              editorMode === 'edit' ? editTarget?.targetFilter : undefined
            }
            initialSourcePath={
              editorMode === 'edit' ? editTarget?.sourcePath : undefined
            }
            addedSource="cell_popover_add_another"
          />
        );
        return relationshipPort ? (
          <HostRelationshipTables providers={relationshipPort} connectionId={connectionId} database={database}>
            {editorDialog}
          </HostRelationshipTables>
        ) : editorDialog(allTables);
      })()}
    </>
  );
}
